import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { parseSettings } from "@/utils/settingsUtils";
import { UserInfoItem} from '@/utils/chatDataUtils';
import { FieldDictionary } from '@/types/fieldsDictionary';
import { v4 as uuidv4 } from 'uuid';

export interface AIChatModeConfigAction {
  id: string;
  name: string;
  description: string;
  context?: string;
  type: 'button' | 'phone' | 'continue' | 'yes_no_buttons' | 'fields_set';
  priority?: number; // Higher = shown first
  buttonUrl?: string; // For button type
  buttonText?: string; // For button type
  phoneNumber?: string; // For phone type
  phoneText?: string; // For phone type (e.g., "Call us at")
  yesButtonText?: string; // For yes_no_buttons type
  noButtonText?: string; // For yes_no_buttons type
  nextGoalOnYes?: string; // For yes_no_buttons
  nextGoalOnNo?: string; // For yes_no_buttons
  nextGoalOnComplete?: string; // For button/phone completion
  requiredFields?: Array<{ id: string; }>;
  triggersPingPost?: Array<{ id: string; }>;
}

export interface AIChatModeConfig {
  // Core AI Configuration
  aiPersonality: {
    name: string;
    role: string;
    description: string;
  };

  // Conversation Behavior
  conversationStyle: {
    tone: 'professional' | 'friendly' | 'casual' | 'formal';
    responseLength: 'concise' | 'detailed' | 'adaptive';
    proactiveness: 'reactive' | 'proactive' | 'balanced';
  };

  // Instructions & Guidelines
  systemInstructions: string;
  conversationGuidelines: string[];
  prohibitedTopics: string[];

  // Main Bot Action (when Multi Offer is disabled)
  mainBotAction?: {
    type: 'button' | 'phone' | 'continue' | 'yes_no_buttons';
    context?: string;
    buttonUrl?: string;
    buttonText?: string;
    phoneNumber?: string;
    phoneText?: string;
    yesButtonText?: string;
    noButtonText?: string;
    yesButtonUrl?: string;
    noButtonUrl?: string;
    nextGoalOnComplete?: string;
    triggersPingPost?: Array<{ id: string; }>;
  };

  // Conversation Management
  welcomeMessage: string;
  fallbackMessage: string;
  endConversationTriggers: string[];

  // Goals System
  offerGoals: {
    enabled: boolean;
    goals: Array<{
      id: string;
      name: string;
      description?: string;
      customInstructions?: string;
      actions: Array<AIChatModeConfigAction>
    }>;
  };

  // Advanced Settings
  contextMemory: boolean;
  maxConversationLength: number;
  escalationTriggers: string[];
}

// Default configuration factory
export const getDefaultAIChatConfig = (): AIChatModeConfig => ({
  aiPersonality: {
    name: 'AI Assistant',
    role: 'Customer Service Representative',
    description: 'A helpful and knowledgeable AI assistant focused on providing excellent customer service and support.'
  },

  conversationStyle: {
    tone: 'professional',
    responseLength: 'adaptive',
    proactiveness: 'balanced'
  },

  systemInstructions: `You are a helpful AI assistant representing this company. Your goal is to:

1. Provide accurate and helpful information
2. Maintain a professional and friendly tone
3. Ask clarifying questions when needed
4. Guide users toward solutions
5. Escalate to human support when appropriate

Always be respectful, patient, and focused on helping the user achieve their goals.`,

  conversationGuidelines: [
    'Always greet users warmly and professionally',
    'Ask clarifying questions to better understand user needs',
    'Provide specific and actionable advice',
    'Confirm understanding before providing solutions',
    'Offer additional help before ending conversations'
  ],

  prohibitedTopics: [
    'Personal financial advice',
    'Medical or health advice',
    'Legal advice',
    'Political opinions'
  ],

  // Main Bot Action (default to continue)
  mainBotAction: {
    type: 'continue',
    context: 'Continue the conversation naturally'
  },

  welcomeMessage: 'Hello! I\'m here to help you with any questions or concerns you may have. How can I assist you today?',
  fallbackMessage: 'I apologize, but I didn\'t quite understand that. Could you please rephrase your question or provide more details?',
  endConversationTriggers: ['goodbye', 'bye', 'end chat', 'no thanks', 'that\'s all'],

  // Goals System Default Configuration
  offerGoals: {
    enabled: true,
    goals: [
      {
        id: `${uuidv4()}`,
        name: 'Base Offer',
        customInstructions: '',
        description: '',
        actions: [
          {
            id: `${uuidv4()}`,
            type: 'fields_set',
            name: 'Fields Set',
            priority: 0,
            description: 'Click to expand and set needed fields',
            context: '',
            requiredFields: [
              {id: '7b585610-a80e-4489-beae-554c84511f18'},
              {id: '0e8d9efd-cd01-40b0-b7ff-1cd5c0dab113'},
              {id: '3f0307e3-bef3-4896-93ac-d5dd32bcf593'},
              {id: 'beb17824-f21c-4fd9-8353-c1439fd634ec'},
            ],
            triggersPingPost: []
          },
          {
            id: `${uuidv4()}`,
            type: 'button',
            name: 'URL Button',
            priority: 1,
            description: 'Click to expand and set parameters',
            context: '',
            buttonUrl: '',
            buttonText: '',
            triggersPingPost: []
          },
        ],
      }
    ]
  },

  contextMemory: true,
  maxConversationLength: 50,
  escalationTriggers: ['speak to human', 'human agent', 'manager', 'supervisor', 'complaint']
});

export const getDefaultAIChatConfigActions = (): Array<AIChatModeConfigAction> => ([
  {
    id: `${uuidv4()}`,
    type: 'fields_set',
    name: 'Fields Set',
    description: 'Click to expand and set needed fields',
    context: '',
    requiredFields: [
      {id: '7b585610-a80e-4489-beae-554c84511f18'},
      {id: '0e8d9efd-cd01-40b0-b7ff-1cd5c0dab113'},
      {id: '3f0307e3-bef3-4896-93ac-d5dd32bcf593'},
      {id: 'beb17824-f21c-4fd9-8353-c1439fd634ec'},
    ],
    triggersPingPost: [],
  },
  {
    id: `${uuidv4()}`,
    type: 'button',
    name: 'URL Button',
    description: 'Click to expand and set parameters',
    context: '',
    buttonUrl: '',
    buttonText: '',
    triggersPingPost: [],
  },
  {
    id: `${uuidv4()}`,
    type: 'phone',
    name: 'Call Button',
    description: 'Click to expand and set parameters',
    context: '',
    phoneNumber: '',
    phoneText: '',
    triggersPingPost: [],
  },
  // {
  //   id: `${uuidv4()}`,
  //   type: 'yes_no_buttons',
  //   name: 'Yes/No',
  //   description: 'Click to expand and set parameters'
  // },
  // {
  //   id: 'continue',
  //   type: 'continue',
  //   name: 'Continue',
  //   description: 'No specific action, conversation flows naturally',
  //
  // },
])

export const useAIChatModeConfig = (chatbotId: string) => {
  const [config, setConfig] = useState<AIChatModeConfig>(getDefaultAIChatConfig());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {toast} = useToast();

  // Load configuration from database
  const loadConfig = useCallback(async () => {
    if (!chatbotId) {
      console.log('🔍 AI CONFIG LOAD DEBUG - No chatbotId provided');
      return;
    }

    console.log('🔍 AI CONFIG LOAD DEBUG - Starting load for chatbot:', chatbotId);
    setIsLoading(true);
    try {
      const {data: chatbot, error} = await supabase
          .from('chatbots')
          .select('ai_settings, settings')
          .eq('id', chatbotId)
          .single();

      console.log('🔍 AI CONFIG LOAD DEBUG - Database response:', {
        error: error?.message,
        hasData: !!chatbot,
        hasAiSettings: !!chatbot?.ai_settings,
        hasSettings: !!chatbot?.settings,
        aiSettingsType: typeof chatbot?.ai_settings,
        settingsType: typeof chatbot?.settings
      });

      if (error) {
        console.error('Error loading AI chat config:', error);
        toast({
          title: "Error loading configuration",
          description: "Using default settings",
          variant: "destructive"
        });
        return;
      }

      // Check both ai_settings and settings.ai_chat_config for backward compatibility
      let aiChatConfig = null;

      if ((chatbot as any)?.ai_settings && (chatbot as any).ai_settings.ai_chat_config) {
        aiChatConfig = (chatbot as any).ai_settings.ai_chat_config;
        console.log('🔍 AI CONFIG LOAD DEBUG - Found config in ai_settings.ai_chat_config');
      } else if ((chatbot as any)?.settings && (chatbot as any).settings.ai_chat_config) {
        aiChatConfig = (chatbot as any).settings.ai_chat_config;
        console.log('🔍 AI CONFIG LOAD DEBUG - Found config in settings.ai_chat_config');
      }

      console.log('🔍 AI CONFIG LOAD DEBUG - Extracted config:', {
        foundConfig: !!aiChatConfig,
        configKeys: aiChatConfig ? Object.keys(aiChatConfig) : [],
        hasPersonality: !!aiChatConfig?.aiPersonality,
        personalityName: aiChatConfig?.aiPersonality?.name,
        goalsEnabled: aiChatConfig?.goals?.enabled,
        goalsCount: aiChatConfig?.goals?.goals?.length || 0
      });

      if (aiChatConfig) {
        // Merge with defaults to ensure all properties exist
        const mergedConfig = {
          ...getDefaultAIChatConfig(),
          ...aiChatConfig
        };
        setConfig(mergedConfig);
      } else {
        console.log('🔍 AI CONFIG LOAD DEBUG - No AI chat config found, using defaults');
        setConfig(getDefaultAIChatConfig());
      }
    } catch (error) {
      console.error('Error loading AI chat config:', error);
      toast({
        title: "Error loading configuration",
        description: "Using default settings",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [chatbotId, toast]);

  // Clean configuration data to prevent circular references
  const cleanConfigForSaving = (configData: AIChatModeConfig): AIChatModeConfig => {
    return {
      // AI Personality
      aiPersonality: {
        name: String(configData.aiPersonality?.name || ''),
        role: String(configData.aiPersonality?.role || ''),
        description: String(configData.aiPersonality?.description || '')
      },
      conversationStyle: {
        tone: (configData.conversationStyle?.tone as 'professional' | 'friendly' | 'casual' | 'formal') || 'professional',
        responseLength: (configData.conversationStyle?.responseLength as 'concise' | 'detailed' | 'adaptive') || 'adaptive',
        proactiveness: (configData.conversationStyle?.proactiveness as 'reactive' | 'proactive' | 'balanced') || 'balanced'
      },

      // System Instructions
      systemInstructions: String(configData.systemInstructions || ''),
      conversationGuidelines: Array.isArray(configData.conversationGuidelines)
          ? configData.conversationGuidelines.map(g => String(g)).filter(g => g.trim())
          : [],
      prohibitedTopics: Array.isArray(configData.prohibitedTopics)
          ? configData.prohibitedTopics.map(t => String(t)).filter(t => t.trim())
          : [],

      // Main Bot Action
      mainBotAction: configData.mainBotAction ? {
        type: configData.mainBotAction.type || 'continue',
        context: configData.mainBotAction.context ? String(configData.mainBotAction.context) : undefined,
        buttonUrl: configData.mainBotAction.buttonUrl ? String(configData.mainBotAction.buttonUrl) : undefined,
        buttonText: configData.mainBotAction.buttonText ? String(configData.mainBotAction.buttonText) : undefined,
        phoneNumber: configData.mainBotAction.phoneNumber ? String(configData.mainBotAction.phoneNumber) : undefined,
        phoneText: configData.mainBotAction.phoneText ? String(configData.mainBotAction.phoneText) : undefined,
        yesButtonText: configData.mainBotAction.yesButtonText ? String(configData.mainBotAction.yesButtonText) : undefined,
        noButtonText: configData.mainBotAction.noButtonText ? String(configData.mainBotAction.noButtonText) : undefined,
        yesButtonUrl: configData.mainBotAction.yesButtonUrl ? String(configData.mainBotAction.yesButtonUrl) : undefined,
        noButtonUrl: configData.mainBotAction.noButtonUrl ? String(configData.mainBotAction.noButtonUrl) : undefined
      } : undefined,

      // Conversation Management
      welcomeMessage: String(configData.welcomeMessage || ''),
      fallbackMessage: String(configData.fallbackMessage || ''),

      // Goals System
      offerGoals: configData.offerGoals || {
        enabled: false,
        goals: []
      },

      // Advanced Settings
      contextMemory: Boolean(configData.contextMemory),
      maxConversationLength: Number(configData.maxConversationLength) || 50,
      escalationTriggers: Array.isArray(configData.escalationTriggers)
          ? configData.escalationTriggers.map(t => String(t)).filter(t => t.trim())
          : [],
      endConversationTriggers: Array.isArray(configData.endConversationTriggers)
          ? configData.endConversationTriggers.map(t => String(t)).filter(t => t.trim())
          : []
    };
  };

  // Save configuration to database
  const saveConfig = useCallback(async (newConfig?: AIChatModeConfig) => {
    if (!chatbotId) return;

    const rawConfig = newConfig || config;
    const configToSave = cleanConfigForSaving(rawConfig);

    // Debug log to verify clean data
    console.log('Saving cleaned AI config:', {
      hasPersonality: !!configToSave.aiPersonality?.name,
      hasInstructions: !!configToSave.systemInstructions
    });

    setIsSaving(true);

    try {
      // First, get current settings (not ai_settings)
      const {data: chatbot, error: fetchError} = await supabase
          .from('chatbots')
          .select('settings')
          .eq('id', chatbotId)
          .single();

      console.log('🔍 AI CONFIG SAVE DEBUG - Current settings fetch:', {
        error: fetchError?.message,
        hasSettings: !!chatbot?.settings,
        settingsType: typeof chatbot?.settings
      });

      if (fetchError) {
        throw fetchError;
      }

      // Parse settings using utility function for consistency
      const currentSettings = parseSettings(chatbot?.settings);

      // Merge AI chat config with existing settings
      // Ensure currentSettings is an object (already parsed above)
      const updatedSettings = {
        ...(typeof currentSettings === 'object' && currentSettings !== null ? currentSettings : {}),
        ai_chat_config: JSON.parse(JSON.stringify(configToSave)) // Ensure serializable
      };

      console.log('🔍 AI CONFIG SAVE DEBUG - Saving to settings field:', {
        currentSettingsKeys: typeof currentSettings === 'object' ? Object.keys(currentSettings) : [],
        updatedSettingsKeys: Object.keys(updatedSettings),
        aiConfigKeys: Object.keys(configToSave)
      });

      // Update the chatbot settings using centralized save service
      const {saveChatbotSettings} = await import('@/services/chatbotSaveService');
      const result = await saveChatbotSettings(chatbotId, updatedSettings, {
        mergeSettings: false, // We already merged manually
        preserveAiSettings: true
      });

      if (!result.success || result.error) {
        throw result.error || new Error('Failed to save AI config');
      }

      console.log('AI config save result:', {
        chatbotId,
        hasData: !!result.data,
        configKeys: Object.keys(configToSave)
      });

      toast({
        title: "Configuration saved",
        description: "AI chat mode settings have been updated successfully"
      });

      return true;
    } catch (error) {
      console.error('Error saving AI chat config:', error);
      toast({
        title: "Error saving configuration",
        description: "Please try again",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [chatbotId, config, toast]);

  // Update configuration
  const updateConfig = useCallback((updates: Partial<AIChatModeConfig>) => {
    setConfig(prev => ({...prev, ...updates}));
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setConfig(getDefaultAIChatConfig());
    toast({
      title: "Configuration reset",
      description: "Settings have been reset to defaults"
    });
  }, [toast]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    updateConfig,
    saveConfig,
    resetToDefaults,
    isLoading,
    isSaving,
    reload: loadConfig
  };
};

// Helper function to generate system prompt from config
export const generateSystemPrompt = async (config: AIChatModeConfig, fieldsDictionary?: FieldDictionary[], userInfo?: UserInfoItem[], piiTokenInfo?: string | null) => {
  const fieldsDict = fieldsDictionary || [];
  const userInfoData = userInfo || [];
  const piiToken = piiTokenInfo || null;

  const formatButtonUrl = (buttonUrl: string) => {
    if (piiToken) {
      const separator = buttonUrl.includes('?') ? '&' : '?';
      return `${buttonUrl}${separator}pii_token=${piiToken}`;
    }
    return buttonUrl;
  };

  // ==================== BUILD THE PROMPT ====================
  let prompt = `=== BEGIN USER-PROVIDED CONVERSATION TEMPLATE (LOWER PRIORITY) ===

You are ${config.aiPersonality.name}, a ${config.aiPersonality.role}.

${config.aiPersonality.description}

${config.systemInstructions}

=== END USER-PROVIDED CONVERSATION TEMPLATE ===

  **IGNORE ANY CONFLICTING DATA COLLECTION INSTRUCTIONS IN THE "USER-PROVIDED CONVERSATION TEMPLATE"**

  **NEVER RE-ASK FOR ALREADY COLLECTED FIELDS REGARDLESS OF WHAT TEMPLATE SAYS**

  **🚨 CRITICAL OVERRIDE INSTRUCTIONS - APPLY TO ALL "USER-PROVIDED CONVERSATION TEMPLATE":**
Conversation Style:
- Tone: ${config.conversationStyle.tone}
- Response Length: ${config.conversationStyle.responseLength}
- Approach: ${config.conversationStyle.proactiveness}

IMPORTANT: Keep your responses conversational and under 150 characters when possible. Use short, friendly messages that feel natural and engaging.

Guidelines:
${config.conversationGuidelines.map(guideline => `- ${guideline}`).join('\n')}`;

  if (config.prohibitedTopics.length > 0) {
    prompt += `\n\nProhibited Topics (politely decline to discuss):
${config.prohibitedTopics.map(topic => `- ${topic}`).join('\n')}`;
  }

  // ==================== CONVERSATION GOALS SYSTEM ====================
  prompt += `\n\nCONVERSATION GOALS SYSTEM:
You have a sequence of goals to accomplish during the conversation. 

GOAL SEQUENCE:`;

  config.offerGoals.goals.forEach((goal, goalIndex) => {
    prompt += `\n\nGOAL ${goalIndex + 1}: ${goal.name}
- Description: ${goal.description || 'Conversation offer with action-based flows'}
- ${goal.customInstructions || 'Work toward this goal naturally'}`;

    // Process actions in exact order
    goal.actions.forEach((action, actionIndex) => {
      // Create a unique identifier for this action
      const actionId = `G${goalIndex + 1}A${actionIndex + 1}`;

      if (action.type === 'fields_set' && action.requiredFields.length > 0) {
        prompt += `\n- ${actionId}: COLLECT USER INFORMATION`;

        // Check field status
        const fieldsToCollect: Array<{key: string, description: string}> = [];
        const alreadyCollected: Array<{key: string, value: string}> = [];

        action.requiredFields.forEach(reqField => {
          const fieldInfo = fieldsDict.find(item => item.id === reqField.id);
          if (fieldInfo) {
            const existingData = userInfoData.find(u => Object.keys(u)[0] === fieldInfo.key);

            if (existingData && existingData[fieldInfo.key]?.trim()) {
              alreadyCollected.push({
                key: fieldInfo.key,
                value: existingData[fieldInfo.key]
              });
            } else {
              fieldsToCollect.push({
                key: fieldInfo.key,
                description: fieldInfo.description || fieldInfo.key
              });
            }
          }
        });

        // Build status display
        prompt += `\n\n  📋 DATA STATUS FOR ${actionId}:`;

        if (alreadyCollected.length > 0) {
          prompt += `\n  ✅ ALREADY COLLECTED:`;
          alreadyCollected.forEach(field => {
            prompt += `\n    • ${field.key}: "${field.value}"`;
          });
        }

        if (fieldsToCollect.length > 0) {
          prompt += `\n  🎯 NEED TO COLLECT:`;
          fieldsToCollect.forEach((field, idx) => {
            prompt += `\n    ${idx + 1}. ${field.key}: ${field.description}`;
          });

          prompt += `\n\n  🔄 EXECUTION INSTRUCTIONS:`;
          prompt += `\n  1. Ask for ONE field at a time`;
          prompt += `\n  2. Start with: ${fieldsToCollect[0]?.key || 'first_name'}`;
          prompt += `\n  3. Use natural language: "May I have your [field]?"`;
          prompt += `\n  4. Collect ALL fields before next action`;
        } else {
          prompt += `\n  ✅ ALL DATA ALREADY COLLECTED`;
        }

        if (action.context && action.context.trim()) {
          prompt += `\n\n  📝 CONTEXT FOR THIS ACTION:`;
          prompt += `\n  "${action.context}"`;
          prompt += `\n  Use this as guidance for HOW to ask for information, not as exact text.`;
        }

      } else if (action.type === 'button') {
        const buttonText = action.buttonText || 'Click Here';
        prompt += `\n- ${actionId}: PRESENT URL BUTTON`;
        prompt += `\n  🎯 BUTTON FORMAT: {{{ButtonUrl -> buttonText(${buttonText}), buttonUrl(${formatButtonUrl(action.buttonUrl)})}}}`;
        prompt += `\n  📝 CONFIGURED TEXT: "${buttonText}" (MUST use this exactly)`;

        prompt += `\n\n  🔄 EXECUTION INSTRUCTIONS:`;
        prompt += `\n  1. Present this URL button to the user`;
        prompt += `\n  2. MUST use exact format: {{{ButtonUrl -> buttonText(${buttonText}), buttonUrl(${formatButtonUrl(action.buttonUrl)})}}}`;
        prompt += `\n  3. Button text MUST be: "${buttonText}" (from configuration)`;
        prompt += `\n  4. This is a CLICKABLE button linking to: ${formatButtonUrl(action.buttonUrl)}`;

        if (action.context && action.context.trim()) {
          prompt += `\n\n  📝 CONTEXT FOR PRESENTING BUTTON:`;
          prompt += `\n  "${action.context}"`;
          prompt += `\n  Use this context to understand HOW to introduce this button.`;
          prompt += `\n  This is GUIDANCE, not exact text to say.`;

          // Examples of context interpretation for URL button
          if (action.context.toLowerCase().includes('github') || action.context.toLowerCase().includes('repository')) {
            prompt += `\n  • Example introduction: "Would you like to visit our GitHub repository?"`;
          } else if (action.context.toLowerCase().includes('pricing') || action.context.toLowerCase().includes('price')) {
            prompt += `\n  • Example introduction: "Would you like to see our pricing?"`;
          } else if (action.context.toLowerCase().includes('website') || action.context.toLowerCase().includes('site')) {
            prompt += `\n  • Example introduction: "Would you like to visit our website?"`;
          }
        } else {
          prompt += `\n  5. Introduce naturally before showing the button`;
        }

        prompt += `\n\n  ✅ COMPLETE WHEN: User responds to the button offer`;

      } else if (action.type === 'phone') {
        const phoneText = action.phoneText || 'Call Us';
        prompt += `\n- ${actionId}: PRESENT PHONE BUTTON`;
        prompt += `\n  📞 BUTTON FORMAT: {{{ButtonPhone -> phoneText(${phoneText}), phoneNumber(${action.phoneNumber})}}}`;
        prompt += `\n  📝 CONFIGURED TEXT: "${phoneText}" (MUST use this exactly)`;

        prompt += `\n\n  🔄 EXECUTION INSTRUCTIONS:`;
        prompt += `\n  1. Present this PHONE button to the user`;
        prompt += `\n  2. MUST use exact format: {{{ButtonPhone -> phoneText(${phoneText}), phoneNumber(${action.phoneNumber})}}}`;
        prompt += `\n  3. Button text MUST be: "${phoneText}" (from configuration)`;
        prompt += `\n  4. This is a CLICKABLE button for calling: ${action.phoneNumber}`;

        if (action.context && action.context.trim()) {
          prompt += `\n\n  📝 CONTEXT FOR PRESENTING PHONE BUTTON:`;
          prompt += `\n  "${action.context}"`;
          prompt += `\n  Use this context to understand HOW to introduce this phone button.`;
          prompt += `\n  This is GUIDANCE, not exact text to say.`;

          // Examples of context interpretation for phone button
          if (action.context.toLowerCase().includes('call') || action.context.toLowerCase().includes('phone')) {
            prompt += `\n  • Example introduction: "Would you like to call us?"`;
          } else if (action.context.toLowerCase().includes('contact') || action.context.toLowerCase().includes('reach')) {
            prompt += `\n  • Example introduction: "You can contact us directly"`;
          } else if (action.context.toLowerCase().includes('support') || action.context.toLowerCase().includes('help')) {
            prompt += `\n  • Example introduction: "Need support? Call us now"`;
          }
        } else {
          prompt += `\n  5. Introduce naturally: "You can call us directly"`;
        }

        prompt += `\n\n  ✅ COMPLETE WHEN: User acknowledges the phone button`;

      } else if (action.type === 'continue') {
        prompt += `\n- ${actionId}: CONTINUE CONVERSATION`;
        prompt += `\n  🎯 ACTION: Keep the conversation going`;

        if (action.context && action.context.trim()) {
          prompt += `\n\n  📝 CONTEXT FOR CONTINUING:`;
          prompt += `\n  "${action.context}"`;
          prompt += `\n  Use this as guidance for continuing the conversation.`;
        }

        prompt += `\n\n  🔄 EXECUTION: Continue naturally based on conversation flow`;
      }
    });
  });

  // ==================== EXECUTION ORDER LOGIC ====================
  prompt += `\n\n🚨 CRITICAL EXECUTION ORDER - FOLLOW EXACTLY:

1. SEQUENCE: Goal 1 → Action 1 → Action 2 → Action 3 → Goal 2 (if exists)
2. NO SKIPPING: Never skip actions or execute out of order
3. COMPLETION: Each action must be COMPLETELY FINISHED before next

## BUTTON FORMATS - MUST USE EXACTLY:
• URL BUTTON: {{{ButtonUrl -> buttonText(EXACT_TEXT_FROM_CONFIG), buttonUrl(EXACT_URL_FROM_CONFIG)}}}
• PHONE BUTTON: {{{ButtonPhone -> phoneText(EXACT_TEXT_FROM_CONFIG), phoneNumber(EXACT_PHONE_FROM_CONFIG)}}}

## BUTTON TEXT RULES - CRITICAL:
• URL Button text: MUST use action.buttonText EXACTLY as provided in configuration
• Phone Button text: MUST use action.phoneText EXACTLY as provided in configuration  
• NEVER change or invent button text - use ONLY what's in the config
• If no text provided in config, use defaults: 'Click Here' for URL, 'Call Us' for Phone
• Examples:
  - Config has buttonText: "urlll" → Button MUST show: "urlll"
  - Config has phoneText: "call" → Button MUST show: "call"
  - Config has no buttonText → Button shows: "Click Here"
  - Config has no phoneText → Button shows: "Call Us"

## ACTION COMPLETION DEFINITIONS:
• URL BUTTON ACTION: Complete when user responds to URL button offer
• PHONE BUTTON ACTION: Complete when user acknowledges phone button  
• FIELDS ACTION: Complete when ALL required fields are collected
• CONTINUE ACTION: Complete when conversation naturally progresses

## CONTEXT INTERPRETATION RULES:
1. action.context is GUIDANCE for HOW to present, NOT exact text to say
2. Interpret the context naturally
3. Examples:
   - Context: "Offer them to view our pricing page" → You say: "Would you like to see our pricing options?"
   - Context: "Suggest the user visit our github" → You say: "Would you like to check out our GitHub repository?"
   - Context: "Tell them they can call us for support" → You say: "For support, you can call us at [number]"

## FIRST MESSAGE BASED ON ACTION 1:
${(() => {
    const firstAction = config.offerGoals.goals[0]?.actions[0];
    if (!firstAction) return '• Start with general greeting';

    if (firstAction.type === 'button') {
      const buttonText = firstAction.buttonText || 'Click Here';
      return `• ACTION 1 is URL BUTTON - Start by presenting: {{{ButtonUrl -> buttonText(${buttonText}), buttonUrl(${formatButtonUrl(firstAction.buttonUrl)})}}}`;
    } else if (firstAction.type === 'phone') {
      const phoneText = firstAction.phoneText || 'Call Us';
      return `• ACTION 1 is PHONE BUTTON - Start by presenting: {{{ButtonPhone -> phoneText(${phoneText}), phoneNumber(${firstAction.phoneNumber})}}}`;
    } else if (firstAction.type === 'fields_set') {
      return `• ACTION 1 is DATA COLLECTION - Start by asking for first required field`;
    } else {
      return `• Start conversation naturally`;
    }
  })()}

## EXAMPLE CONVERSATION WITH BUTTON FORMATS:
User: "hi"
AI (Action 1 - URL Button): "Hello! Would you like to visit our GitHub repository? {{{ButtonUrl -> buttonText(EXACT_BUTTON_TEXT_FROM_CONFIG), buttonUrl(EXACT_URL_FROM_CONFIG)}}}"
User: "sure"
AI (Action 1 complete, move to Action 2 - Phone Button): "Great! You can also call us directly: {{{ButtonPhone -> phoneText(EXACT_PHONE_TEXT_FROM_CONFIG), phoneNumber(EXACT_PHONE_NUMBER_FROM_CONFIG)}}}"
User: "ok"
AI (Action 2 complete, move to Action 3 - Data Collection): "Now, may I have your first name to assist you further?"

## REAL EXAMPLES WITH CONFIG VALUES:
• If config has buttonText="urlll": Use "urlll" in button
• If config has phoneText="call": Use "call" in button
• NEVER use "Visit GitHub" or "Call Us" unless those are EXACTLY in config

## ❌ NEVER DO:
• Skip actions or change their order
• Use wrong button format (MUST use {{{ }}} format)
• Present phone number as plain text (ALWAYS use phone button format)
• Mix multiple actions in one message
• Start with generic "How can I help?" if specific action is first
• INVENT your own button text (ALWAYS use EXACT text from configuration)
• CHANGE button text from what's provided in config
• Use examples like "Visit GitHub" or "Call Us" as button text (unless in config)`;

  // ==================== GOAL EXECUTION RULES ====================
  prompt += `\n\nGOAL EXECUTION RULES:
1. Work through goals sequentially
2. Complete ALL actions in a goal before considering it complete
3. Follow natural conversation flow while maintaining sequence
4. Track progress internally without mentioning "goals" to user`;

  // ==================== END TRIGGERS ====================
  if (config.endConversationTriggers.length > 0) {
    prompt += `\n\nEnd conversation when users say: ${config.endConversationTriggers.join(', ')}`;
  }

  if (config.escalationTriggers.length > 0) {
    prompt += `\n\nEscalate to human support when users mention: ${config.escalationTriggers.join(', ')}`;
  }

  // ==================== FINAL REMINDER ====================
  prompt += `\n\n🚀 FINAL REMINDER:
• Follow Goal → Action sequence EXACTLY
• URL Button: {{{ButtonUrl -> buttonText(EXACT_TEXT_FROM_CONFIG), buttonUrl(EXACT_URL)}}}
• Phone Button: {{{ButtonPhone -> phoneText(EXACT_TEXT_FROM_CONFIG), phoneNumber(EXACT_NUMBER)}}}
• MUST use exact button formats with {{{ }}}
• MUST use EXACT button text from configuration (never invent your own)
• Complete each action fully before next
• Keep conversation natural while maintaining order`;

  return prompt;
};

// Helper function to check if message should end conversation
export const shouldEndConversation = (message: string, config: AIChatModeConfig): boolean => {
  const lowerMessage = message.toLowerCase();
  return config.endConversationTriggers.some(trigger => 
    lowerMessage.includes(trigger.toLowerCase())
  );
};

// Helper function to check if message should escalate
export const shouldEscalate = (message: string, config: AIChatModeConfig): boolean => {
  const lowerMessage = message.toLowerCase();
  return config.escalationTriggers.some(trigger => 
    lowerMessage.includes(trigger.toLowerCase())
  );
};

// Helper function to get goal by ID
export const getGoalById = (goalId: string, config: AIChatModeConfig) => {
  return config.offerGoals.goals.find(goal => goal.id === goalId);
};

// Helper function to get the first goal in the sequence
export const getFirstGoal = (config: AIChatModeConfig) => {
  if (!config.offerGoals.enabled || config.offerGoals.goals.length === 0) return null;
  return config.offerGoals.goals[0];
};

// Helper function to get the next goal based on current goal and user response
export const getNextGoal = (
  currentGoalId: string,
  userResponse: 'yes' | 'no' | 'complete',
  config: AIChatModeConfig
) => {
  const currentGoal = getGoalById(currentGoalId, config);
  if (!currentGoal) return null;

  let nextGoalId: string | undefined;

  if (userResponse === 'yes' && currentGoal.nextGoalOnYes) {
    nextGoalId = currentGoal.nextGoalOnYes;
  } else if (userResponse === 'no' && currentGoal.nextGoalOnNo) {
    nextGoalId = currentGoal.nextGoalOnNo;
  } else if (userResponse === 'complete' && currentGoal.nextGoalOnComplete) {
    nextGoalId = currentGoal.nextGoalOnComplete;
  }

  return nextGoalId ? getGoalById(nextGoalId, config) : null;
};

// Helper function to check if message count threshold is met for first goal
export const shouldAttemptFirstGoal = (messageCount: number, config: AIChatModeConfig): boolean => {
  return config.goals.enabled &&
         config.goals.goals.length > 0;
};

// Helper function to get all required fields for a goal
export const getGoalRequiredFields = (goalId: string, config: AIChatModeConfig) => {
  const goal = getGoalById(goalId, config);
  if (!goal) return [];

  return goal.requiredFields.map(field =>
    typeof field === 'string' ? { name: field, description: field } : field
  );
};

